import { escapeHtml } from './security';
// Actor Profile Sheet export (PLAN §10.3, task 26a): the entity's live profile laid out in its profile template
// (header, then the numbered blocks), every item with its reliability grade and sources, `assessment` content
// labelled as such, and the export policy of §2 "Sources and attribution" applied: off-record content and the
// identity of protected sources are withheld by default. The same model feeds the DOCX and the PDF (HTML) renderers.

import {
  ATTRIBUTION_LABELS,
  type AttributionLevel,
  type Claim,
  type ClaimKind,
  type EntityProfile,
  formatGrade,
  type IdentityConfidence,
  type ProfileRelation,
  type ProfileValue,
  type ReviewStatus,
  SOURCE_TYPE_LABELS,
  type SourceReliability,
  type SourceType,
} from './claims';
import { gray } from './design/tokens';
import { textDirection } from './graph';
import { WATERMARK_CSS, watermarkLayer } from './report-document';
import { type ResolvedBlock, resolveProfile } from './schema/resolve';
import type { FieldDef, SchemaSnapshot, TableColumn } from './schema/types';
import { formatFieldValue } from './schema/values';

export const EXPORT_FORMATS = ['docx', 'pdf'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];
export const isExportFormat = (f: unknown): f is ExportFormat => EXPORT_FORMATS.includes(f as ExportFormat);
export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = { docx: 'Word (DOCX)', pdf: 'PDF' };
export const EXPORT_MIME: Record<ExportFormat, string> = {
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pdf: 'application/pdf',
};

/** What the export may show beyond the default (PLAN §2: withheld "by default"; only admins may lift it). */
export interface ExportPolicy {
  includeOffRecord: boolean;
  revealProtectedSources: boolean;
}
export const DEFAULT_EXPORT_POLICY: ExportPolicy = { includeOffRecord: false, revealProtectedSources: false };

/** Attribution levels whose source identity is protected: the content is shown, who said it is not. */
export const PROTECTED_ATTRIBUTION: readonly AttributionLevel[] = ['background', 'anonymous', 'off_record'];

/** One piece of provenance of a claim, as read from its evidence (Library document and/or cited source). */
export interface ExportClaimSource {
  document: { id: string; title: string } | null;
  cited: {
    sourceType: SourceType;
    /** Publisher or person, as written or as the linked entity's name. */
    publisher: string | null;
    title: string | null;
    referenceNo: string | null;
    url: string | null;
    publishedAt: string | null;
    identityConfidence: IdentityConfidence | null;
    reliability: SourceReliability | null;
  } | null;
}

export interface SheetExportInput {
  profile: EntityProfile;
  snap: SchemaSnapshot;
  /** The profile's claims in full (periods, amounts, attribution), by id. */
  claims: Record<string, Claim>;
  /** Provenance per claim id. */
  sources: Record<string, ExportClaimSource[]>;
  /** Names for `user:<id>` assessment authors. */
  authors?: Record<string, string>;
  policy?: ExportPolicy;
}

/** One row of a block: its cells, the kind label shown on the value, and the grade/provenance column. */
export interface SheetRow {
  cells: string[];
  /** Index of the cell that carries the kind label (the value, not the label). */
  valueCell: number;
  /** "Assessment — by Analyst team", "Allegation — unverified", "Statement"; null for facts. */
  kindLabel: string | null;
  claimKind: ClaimKind | null;
  /** Grade code ("B2"), or "Not graded". */
  grade: string;
  /** Review status, flags and attribution in words ("Unreviewed", "High importance", "Anonymous source"). */
  flags: string[];
  /** Sources in words; "Source identity withheld" for protected sources. */
  sources: string[];
}

export type SheetPart =
  /** Label/value rows (the template's two-column blocks). */
  | { type: 'fields'; rows: SheetRow[] }
  /** A grid with a header row (the template's tables, Block 4 ratings). */
  | { type: 'table'; caption: string | null; columns: string[]; rows: SheetRow[] }
  /** Labelled prose (Strategic positioning, Analysis & monitoring). */
  | { type: 'text'; rows: SheetRow[] };

export interface SheetBlock {
  key: string;
  /** "BLOCK 4 — INFLUENCE RATINGS", as the template prints it. */
  title: string;
  parts: SheetPart[];
}

export interface ProfileSheetDoc {
  /** "ACTOR PROFILE SHEET". */
  title: string;
  /** "Actor Profile Sheet v2.0". */
  template: string;
  templateVersion: string | null;
  code: string;
  name: string;
  /** Other names in every script ("حسن حداد", "Hassan Hadad"). */
  otherNames: string[];
  type: string;
  /** "ID: MED_X | Camp: Adverse | Status: Active | Reliability: B2 — note | Updated: 2026-09-25". */
  headerLine: string;
  /** "CONFIDENTIAL" when any exported item is confidential (sensitivity travels with the data). */
  marking: string | null;
  blocks: SheetBlock[];
  withheld: { offRecord: number; protectedSources: number };
  policy: ExportPolicy;
  /** Items exported (values, rows and links). */
  items: number;
  /** What grades and labels mean, printed at the end. */
  legend: string[];
}

// ─── Labels ──────────────────────────────────────────────────────────────────

const STATUS_WORDS: Record<ReviewStatus, string | null> = {
  verified: null,
  unreviewed: 'Unreviewed',
  disputed: 'Disputed',
  rejected: 'Rejected',
};
const FLAG_WORDS = { documented: 'documented', semi_documented: 'semi-documented', unverified: 'unverified' } as const;

/** Who an assessment is attributed to, in words: a person's name, "AI extraction (model)", or as written. */
function authorName(a: string, authors: Record<string, string>): string {
  if (authors[a]) return authors[a];
  if (a.startsWith('model:')) return `AI extraction (${a.slice(6)})`;
  if (a.startsWith('user:')) return 'a team member';
  return a;
}

function kindLabel(v: Pick<ProfileValue, 'claimKind' | 'assessedBy' | 'verificationFlag'>, authors: Record<string, string>): string | null {
  switch (v.claimKind) {
    case 'assessment':
      return v.assessedBy ? `Assessment — by ${authorName(v.assessedBy, authors)}` : 'Assessment';
    case 'allegation':
      return `Allegation — ${FLAG_WORDS[v.verificationFlag ?? 'unverified']}`;
    case 'statement':
      return 'Statement';
    default:
      return null;
  }
}

/** "Le Monde — “Title” (Ares(2025)5463115), 2021-10-06 · https://…", or the Library document's title. */
export function describeSource(s: ExportClaimSource): string {
  const c = s.cited;
  if (!c) return s.document ? `Document: ${s.document.title}` : '';
  const who = c.publisher ? `${c.publisher}${c.identityConfidence === 'probable' ? ' (probable)' : ''}` : SOURCE_TYPE_LABELS[c.sourceType];
  const parts = [
    `${who}${c.title ? ` — “${c.title}”` : ''}${c.referenceNo ? ` (${c.referenceNo})` : ''}${c.publishedAt ? `, ${c.publishedAt}` : ''}`,
    c.url ?? '',
    s.document ? `in ${s.document.title}` : '',
  ];
  return parts.filter(Boolean).join(' · ');
}

const uniq = (xs: string[]) => [...new Set(xs.filter(Boolean))];
const isoDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);

// ─── Builder ─────────────────────────────────────────────────────────────────

/** Lays out an entity's live profile in its template, applying the export policy. Pure: no I/O. */
export function buildProfileSheet(input: SheetExportInput): ProfileSheetDoc {
  const { profile, snap, claims, sources } = input;
  const policy = input.policy ?? DEFAULT_EXPORT_POLICY;
  const e = profile.entity;
  const withheld = { offRecord: 0, protectedSources: 0 };
  let items = 0;
  let confidential = e.sensitivity === 'confidential';

  /** The row for one claim, or null when the policy withholds it (counted). */
  const row = (v: ProfileValue, cells: string[], valueCell: number): SheetRow | null => {
    if (v.attributionLevel === 'off_record' && !policy.includeOffRecord) {
      withheld.offRecord++;
      return null;
    }
    items++;
    if (v.sensitivity === 'confidential') confidential = true;
    const claim = claims[v.claimId];
    const own = sources[v.claimId] ?? [];
    const protectedSource = PROTECTED_ATTRIBUTION.includes(v.attributionLevel) && !policy.revealProtectedSources;
    let described = uniq(own.map(describeSource));
    if (protectedSource && described.length) {
      withheld.protectedSources++;
      described = [`Source identity withheld (${ATTRIBUTION_LABELS[v.attributionLevel].toLowerCase()})`];
    }
    // The claim's own grade; otherwise its cited source's reliability letter.
    const citedLetter = own.find(s => s.cited?.reliability)?.cited?.reliability ?? null;
    const grade = v.grade ?? (citedLetter ? formatGrade({ source: citedLetter, credibility: null }) : 'Not graded');
    const flags = [
      STATUS_WORDS[v.status],
      v.highImportance ? 'High importance' : null,
      v.attributionLevel !== 'on_record' ? ATTRIBUTION_LABELS[v.attributionLevel] : null,
      v.sensitivity === 'confidential' ? 'Confidential' : null,
      claim?.translation && claim.originalText ? `Translation: ${claim.translation}` : null,
    ].filter((x): x is string => !!x);
    return { cells, valueCell, kindLabel: kindLabel(v, input.authors ?? {}), claimKind: v.claimKind, grade, flags, sources: described };
  };

  const layout = resolveProfile(snap, e.typeId);
  const template = layout?.template ?? null;
  const shownFields = new Set<string>();
  const tableParts = new Map<string, Extract<SheetPart, { type: 'table' }>>();
  const valueText = (f: FieldDef, v: ProfileValue) =>
    f.dataType === 'table' ? v.display : formatFieldValue(f, v.value, snap) || v.display;

  // ── Header ──
  const headerValues = (key: string) =>
    (profile.fields[key]?.values ?? [])
      .filter(v => v.attributionLevel !== 'off_record' || policy.includeOffRecord)
      .map(v => {
        const def = snap.fields.find(f => f.key === key);
        return def ? formatFieldValue(def, v.value, snap) || v.display : v.display;
      });
  for (const k of ['camp', 'camp_note', 'status', 'status_note']) shownFields.add(k);
  const camp = [...headerValues('camp'), ...headerValues('camp_note')].join(' / ');
  const status = [...headerValues('status'), ...headerValues('status_note')].join(' / ');
  const grade = e.reliability.source || e.reliability.credibility ? formatGrade(e.reliability) : '';
  const reliability = [grade, e.reliability.note].filter(Boolean).join(' — ');
  const headerLine = [
    `ID: ${e.code}`,
    `Camp: ${camp || '—'}`,
    `Status: ${status || '—'}`,
    `Reliability: ${reliability || '—'}`,
    `Updated: ${isoDate(e.updatedAt)}`,
  ].join(' | ');

  // ── Parts for a block's fields ──
  const partsFor = (fields: FieldDef[], mode: 'fields' | 'text' | 'table' | 'ratings'): SheetPart[] => {
    const kv: SheetRow[] = [];
    const tables: SheetPart[] = [];
    for (const f of fields) {
      shownFields.add(f.key);
      const values = profile.fields[f.key]?.values ?? [];
      if (f.dataType === 'table' && f.columns?.length) {
        const cols = f.columns;
        const rows = values.flatMap(v => {
          const r = row(
            v,
            cols.map(c => cellValue(c, (v.value ?? {}) as Record<string, unknown>, snap)),
            0,
          );
          return r ? [r] : [];
        });
        const part: SheetPart = { type: 'table', caption: fields.length > 1 ? f.label : null, columns: cols.map(c => c.label), rows };
        tableParts.set(f.key, part);
        tables.push(part);
        continue;
      }
      if (f.dataType === 'rating' && mode === 'ratings') {
        const rows = values.flatMap(v => {
          const r = v.value as { dimension?: string | null; original?: string; value?: number | null; justification?: string } | null;
          const rating = r ? `${r.original ?? ''}${r.value ? ` (${r.value}/5)` : ''}`.trim() : v.display;
          const out = row(v, [r?.dimension || f.label, rating, r?.justification ?? ''], 1);
          return out ? [out] : [];
        });
        tables.push({ type: 'table', caption: null, columns: ['Dimension', 'Rating', 'Justification'], rows });
        continue;
      }
      const rows = values.flatMap(v => {
        const r = row(v, [f.label, valueText(f, v)], 1);
        return r ? [r] : [];
      });
      // Every label of the template is printed, as in the sheet; an empty one reads "—".
      if (rows.length) kv.push(...rows.map((r, i) => (i === 0 ? r : { ...r, cells: ['', ...r.cells.slice(1)] })));
      else kv.push(emptyRow([f.label, '—']));
    }
    const lead: SheetPart[] = kv.length ? [{ type: mode === 'text' ? 'text' : 'fields', rows: kv }] : [];
    return [...lead, ...tables];
  };

  const blockTitle = (b: Pick<ResolvedBlock, 'number' | 'label'>) =>
    b.number ? `BLOCK ${b.number} — ${b.label.toUpperCase()}` : b.label.toUpperCase();

  const blocks: SheetBlock[] = [];
  for (const b of layout?.blocks ?? []) {
    if (b.layout === 'header') {
      for (const f of b.fields) shownFields.add(f.key);
      continue;
    }
    blocks.push({ key: b.key, title: blockTitle(b), parts: partsFor(b.fields, b.layout) });
  }
  for (const g of layout?.otherGroups ?? []) blocks.push({ key: g.key, title: g.label.toUpperCase(), parts: partsFor(g.fields, 'fields') });

  // ── Links: into the template table that holds their kind (Block 5 flows, 6 participations, 7 relations) ──
  const tableWith = (colKey: string) => {
    for (const b of layout?.blocks ?? [])
      for (const f of b.fields) {
        const part = tableParts.get(f.key);
        if (part && f.columns?.some(c => c.key === colKey)) return { part, columns: f.columns };
      }
    return null;
  };
  const targets = { flow: tableWith('amount'), participation: tableWith('event'), relation: tableWith('linked_actor') };
  const others: SheetRow[] = [];
  const nameOf = (r: ProfileRelation) => (r.otherEntity ? `${r.otherEntity.displayName} (${r.otherEntity.code})` : '');
  for (const r of profile.relations) {
    if (r.category === 'row') continue; // listed in another entity's table: that entity's sheet shows it
    const c = claims[r.claimId];
    const period = c?.periodText ?? ([c?.validFrom, c?.validTo].filter(Boolean).join(' – ') || '');
    const extra = r.value && typeof r.value === 'object' ? (r.value as Record<string, string>) : {};
    const target = targets[r.category as keyof typeof targets] ?? null;
    if (target) {
      const cells = target.columns.map(col => {
        switch (col.key) {
          case 'linked_actor':
          case 'event':
            return nameOf(r);
          case 'source':
            return r.category === 'flow' ? nameOf(r) : '';
          case 'link_types':
          case 'type':
            return [r.label, extra.role].filter(Boolean).join(' — ');
          case 'period':
          case 'date':
            return period;
          case 'amount':
            return c?.amountText ?? '';
          case 'context':
            return extra.note ?? '';
          case 'actors':
            return [e.displayName, extra.role].filter(Boolean).join(' — ');
          case 'verified':
            return r.verificationFlag ? FLAG_WORDS[r.verificationFlag] : r.status === 'verified' ? 'Yes — reviewed' : '';
          default:
            return '';
        }
      });
      const out = row(
        r,
        cells,
        Math.max(
          0,
          target.columns.findIndex(col => ['link_types', 'type', 'event'].includes(col.key)),
        ),
      );
      if (out) target.part.rows.push(out);
      continue;
    }
    const out = row(r, [r.label, nameOf(r), [period, c?.amountText, extra.role, extra.note].filter(Boolean).join(' · ')], 1);
    if (out) others.push(out);
  }
  if (others.length)
    blocks.push({
      key: 'other_links',
      title: 'OTHER LINKS',
      parts: [{ type: 'table', caption: null, columns: ['Link', 'With', 'Details'], rows: others }],
    });

  // Values of fields no longer in the layout (archived, or from before a type change) are still exported.
  const orphans = Object.values(profile.fields).filter(f => !shownFields.has(f.key) && f.key !== 'tag');
  if (orphans.length) {
    const rows = orphans.flatMap(f =>
      f.values.flatMap(v => {
        const r = row(v, [f.label, v.display], 1);
        return r ? [r] : [];
      }),
    );
    if (rows.length) blocks.push({ key: 'other_values', title: 'OTHER VALUES', parts: [{ type: 'fields', rows }] });
  }

  return {
    title: (template?.name ?? `${layout?.type.label ?? 'Entity'} profile`).toUpperCase().replace(/\s+—.*$/, ''),
    template: template ? `${template.name} v${template.version}` : `${layout?.type.label ?? 'Entity'} profile`,
    templateVersion: template?.version ?? null,
    code: e.code,
    name: e.displayName,
    otherNames: uniq(e.names.filter(n => n.value !== e.displayName).map(n => n.value)),
    type:
      layout?.chain
        .map(t => t.label)
        .reverse()
        .join(' › ') ?? e.typeKey,
    headerLine,
    marking: confidential ? 'CONFIDENTIAL' : null,
    blocks,
    withheld,
    policy,
    items,
    legend: [
      'Grade: source reliability A–F (A completely reliable … F cannot be judged) × information credibility 1–6 (1 confirmed … 6 cannot be judged). "Not graded": no grade recorded yet.',
      'Assessment: an analyst’s judgement, attributed to its author, never a documented fact. Allegation: what a source says happened, with its verification flag. Statement: a quote in its original language.',
      'Unreviewed: not yet checked by a reviewer. Disputed: sources disagree; every version is listed.',
    ],
  };
}

const emptyRow = (cells: string[]): SheetRow => ({
  cells,
  valueCell: 1,
  kindLabel: null,
  claimKind: null,
  grade: '',
  flags: [],
  sources: [],
});

function cellValue(col: TableColumn, row: Record<string, unknown>, snap: SchemaSnapshot): string {
  const v = row[col.key];
  if (v === undefined || v === null || v === '') return '';
  const def = { dataType: col.type === 'event_ref' ? ('entity_ref' as const) : col.type, enumList: col.enumList ?? null };
  return formatFieldValue(def, v, snap);
}

/** Withheld-content summary in words, for the export screen and the end of the document. */
export function withheldSummary(w: ProfileSheetDoc['withheld']): string | null {
  const parts = [
    w.offRecord ? `${w.offRecord} off-record item${w.offRecord === 1 ? '' : 's'} left out` : null,
    w.protectedSources ? `source identity withheld on ${w.protectedSources} item${w.protectedSources === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return parts.length ? `${parts.join('; ')} (export policy).` : null;
}

/** Download name: `MED_FRANCTIREUR_profile_2026-09-25.docx`. */
export const exportFileName = (code: string, format: ExportFormat, at = Date.now()) =>
  `${code.replace(/[^\w-]/g, '_')}_profile_${isoDate(at)}.${format}`;

/** "ai@socialstudio.me · 2026-09-25 14:03 UTC · Export 01K…" (PLAN §10.4: user + timestamp + export id). */
export const exportWatermark = (email: string, at: number, exportId: string) =>
  `Exported by ${email} · ${new Date(at).toISOString().slice(0, 16).replace('T', ' ')} UTC · Export ${exportId}`;

// ─── HTML (the page Browser Rendering prints to PDF) ─────────────────────────

const esc = escapeHtml;
/** Data text keeps its own direction (Arabic values in an English sheet). */
const data = (s: string, tag = 'span') => (s ? `<${tag} dir="${textDirection(s)}">${esc(s)}</${tag}>` : `<${tag}></${tag}>`);

function noteHtml(r: SheetRow): string {
  if (!r.grade && !r.flags.length && !r.sources.length) return '';
  const grade = r.grade ? `<span class="grade">${esc(r.grade)}</span>` : '';
  const flags = r.flags.length ? ` ${r.flags.map(f => `<span class="flag">${data(f)}</span>`).join(' ')}` : '';
  const src = r.sources.map(s => `<div class="src">${data(s)}</div>`).join('');
  return `${grade}${flags}${src}`;
}

function valueHtml(r: SheetRow, i: number): string {
  const cell = r.cells[i] ?? '';
  const label = i === r.valueCell && r.kindLabel ? `<span class="kind">${esc(r.kindLabel)}</span> ` : '';
  return `${label}${data(cell)}`;
}

function partHtml(p: SheetPart): string {
  if (p.type === 'text')
    return p.rows
      .map(
        r =>
          `<div class="text-item${r.claimKind === 'assessment' ? ' assessment' : ''}"><div class="label">${esc(r.cells[0] ?? '')}</div>` +
          `<div class="prose">${valueHtml(r, 1)}</div>${noteHtml(r) ? `<div class="note">${noteHtml(r)}</div>` : ''}</div>`,
      )
      .join('');
  if (p.type === 'fields')
    return `<table class="kv"><colgroup><col class="c-label"><col><col class="c-note"></colgroup><tbody>${p.rows
      .map(
        r =>
          `<tr${r.claimKind === 'assessment' ? ' class="assessment"' : ''}><th scope="row">${esc(r.cells[0] ?? '')}</th><td>${valueHtml(r, 1)}</td><td class="note">${noteHtml(r)}</td></tr>`,
      )
      .join('')}</tbody></table>`;
  const head = `<tr>${p.columns.map(c => `<th scope="col">${esc(c)}</th>`).join('')}<th scope="col" class="c-note">Grade · sources</th></tr>`;
  const body = p.rows.length
    ? p.rows
        .map(
          r =>
            `<tr${r.claimKind === 'assessment' ? ' class="assessment"' : ''}>${p.columns.map((_, i) => `<td>${valueHtml(r, i)}</td>`).join('')}<td class="note">${noteHtml(r)}</td></tr>`,
        )
        .join('')
    : `<tr><td colspan="${p.columns.length + 1}" class="empty">—</td></tr>`;
  return `${p.caption ? `<h3>${esc(p.caption)}</h3>` : ''}<table class="grid"><thead>${head}</thead><tbody>${body}</tbody></table>`;
}

/** The sheet as a standalone, script-free HTML page (A4, neutral greys from the design tokens). */
export function renderSheetHtml(doc: ProfileSheetDoc, watermark: string): string {
  const withheld = withheldSummary(doc.withheld);
  const css = `
@page { size: A4; margin: 18mm 14mm 20mm; }
* { box-sizing: border-box; }
body { font-family: "Noto Sans", "Noto Naskh Arabic", "Geeza Pro", Arial, sans-serif; font-size: 9.5pt; line-height: 1.4; color: ${gray[950]}; margin: 0; }
h1 { font-size: 16pt; letter-spacing: .04em; margin: 0 0 2pt; }
.name { font-size: 13pt; font-weight: 600; margin: 0; }
.names, .type { color: ${gray[600]}; margin: 2pt 0 0; }
.header-line { border: 1px solid ${gray[400]}; padding: 4pt 6pt; margin: 8pt 0 4pt; font-weight: 600; }
.marking { font-weight: 700; letter-spacing: .08em; margin: 0 0 4pt; }
.meta { color: ${gray[600]}; font-size: 8pt; margin: 0 0 10pt; }
h2 { font-size: 10.5pt; letter-spacing: .03em; border-bottom: 1.5px solid ${gray[900]}; padding-bottom: 2pt; margin: 14pt 0 5pt; break-after: avoid; }
h3 { font-size: 9.5pt; margin: 6pt 0 3pt; }
table { width: 100%; border-collapse: collapse; margin: 0 0 4pt; }
th, td { border: 1px solid ${gray[300]}; padding: 3pt 4pt; vertical-align: top; text-align: start; }
thead th { background: ${gray[100]}; font-weight: 600; }
tr { break-inside: avoid; }
.kv th { width: 26%; font-weight: 600; background: ${gray[50]}; }
.c-label { width: 26%; } .c-note { width: 24%; }
td.note, th.c-note, .text-item .note { color: ${gray[700]}; font-size: 8pt; }
.grade { font-weight: 700; color: ${gray[950]}; }
.flag { border: 1px solid ${gray[400]}; border-radius: 2pt; padding: 0 2pt; margin-inline-start: 2pt; white-space: nowrap; }
.src { margin-top: 1pt; overflow-wrap: anywhere; }
.kind { font-weight: 700; font-style: italic; text-transform: uppercase; font-size: 7.5pt; letter-spacing: .04em; border: 1px solid ${gray[600]}; padding: 0 2pt; }
tr.assessment td:not(.note), .text-item.assessment .prose { font-style: italic; }
.text-item { border-inline-start: 2px solid ${gray[300]}; padding: 2pt 0 2pt 6pt; margin: 0 0 5pt; break-inside: avoid; }
.text-item .label { font-weight: 600; }
.prose span { white-space: pre-wrap; }
.empty { color: ${gray[500]}; }
.policy { margin-top: 12pt; border-top: 1px solid ${gray[400]}; padding-top: 4pt; color: ${gray[700]}; font-size: 8pt; }
.policy p { margin: 2pt 0; }
${WATERMARK_CSS}`;
  const blocks = doc.blocks
    .map(b => `<section><h2>${esc(b.title)}</h2>${b.parts.length ? b.parts.map(partHtml).join('') : '<p class="empty">—</p>'}</section>`)
    .join('');
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(`${doc.code} — ${doc.template}`)}</title><style>${css}</style></head>
<body>
${watermarkLayer(watermark)}
<main class="hp-content">
<h1>${esc(doc.title)}</h1>
<p class="name" dir="${textDirection(doc.name)}">${esc(doc.name)}</p>
${doc.otherNames.length ? `<p class="names">Also known as: ${doc.otherNames.map(n => data(n)).join(' · ')}</p>` : ''}
<p class="type">${esc(doc.type)}</p>
<div class="header-line">${data(doc.headerLine)}</div>
${doc.marking ? `<p class="marking">${esc(doc.marking)}</p>` : ''}
<p class="meta">${esc(doc.template)} · exported from live data · ${esc(watermark)}</p>
${blocks}
<div class="policy">${withheld ? `<p><strong>Withheld:</strong> ${esc(withheld)}</p>` : ''}${doc.legend.map(l => `<p>${esc(l)}</p>`).join('')}</div>
</main>
</body></html>`;
}
